import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useDrag, useDragLayer } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useTouchDrag } from '../../hooks/useTouchDrag';
import { usePieceOverlay } from '../../hooks/usePieceOverlay';
import { getPieceBackgroundPosition } from '../../lib/gameUtils';
import { GRID_SIZE } from '../../lib/constants';
import { cn } from '../../lib/utils';

export const ItemTypes = {
  PUZZLE_PIECE: 'puzzlePiece'
};

// Minimum distance (px) to move before starting a drag - prevents accidental drags on taps
const DRAG_THRESHOLD = 10;

export function PuzzlePiece({ 
  piece, 
  pieceSize, 
  isInTray = false, 
  sourcePosition,
  gridSize = GRID_SIZE,
  useTapMode = false,
  isSelected = false,
  onTap,
  isHinted = false
}) {
  const ref = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [isTouchActive, setIsTouchActive] = useState(false);
  const touchStartRef = useRef(null);
  const hasDragStartedRef = useRef(false);
  
  // Get touch drag handlers from context
  const touchDrag = useTouchDrag();
  
  // Check if mouse is within center hot zone (inner 50% of piece)
  const isInCenterHotZone = useCallback((e) => {
    if (!ref.current) return false;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const hotZoneRadius = rect.width * 0.25; // 25% radius = 50% diameter hot zone
    const distance = Math.sqrt(
      Math.pow(e.clientX - centerX, 2) + Math.pow(e.clientY - centerY, 2)
    );
    return distance <= hotZoneRadius;
  }, []);
  
  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: ItemTypes.PUZZLE_PIECE,
    item: () => ({ 
      id: piece.id, 
      piece, 
      pieceSize,
      fromTray: isInTray,
      sourceRow: sourcePosition?.row,
      sourceCol: sourcePosition?.col,
      gridSize
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    canDrag: () => isInTray || !piece.isCorrect
  }), [piece, isInTray, pieceSize, sourcePosition, gridSize]);
  
  // Hide the native drag preview - we use CustomDragLayer instead
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);
  
  drag(ref);
  
  const backgroundPosition = getPieceBackgroundPosition(piece, pieceSize, gridSize);
  const totalSize = pieceSize * gridSize;
  
  // Calculate display size (2x on hover in tray, normal otherwise)
  const displaySize = isInTray && isHovered && !isDragging ? pieceSize * 2 : pieceSize;
  const scale = displaySize / pieceSize;
  
  // Mouse move handler to check hot zone while hovering
  const handleMouseMove = useCallback((e) => {
    if (!isInTray) return;
    const inHotZone = isInCenterHotZone(e);
    setIsHovered(inHotZone);
  }, [isInTray, isInCenterHotZone]);

  // Touch event handlers for mobile
  const handleTouchStart = useCallback((e) => {
    if (piece.isCorrect && !isInTray) return; // Can't drag correct pieces
    if (!touchDrag) return;
    
    const touch = e.touches[0];
    // Store initial touch position - don't start drag yet
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    hasDragStartedRef.current = false;
    setIsTouchActive(true);
    
    // Prevent default to stop browser gestures (like swipe-back navigation)
    // This is critical to prevent accidental navigation on mobile
    e.preventDefault();
    e.stopPropagation();
  }, [piece, isInTray, touchDrag]);
  
  const handleTouchMove = useCallback((e) => {
    if (!touchStartRef.current || !touchDrag) return;
    if (piece.isCorrect && !isInTray) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Only start drag if moved beyond threshold
    if (!hasDragStartedRef.current && distance >= DRAG_THRESHOLD) {
      hasDragStartedRef.current = true;
      setIsTouchDragging(true);
      touchDrag.startDrag(piece, isInTray, sourcePosition?.row, sourcePosition?.col, touch.clientX, touch.clientY);
    }
    
    // Always prevent default when touching a piece to avoid scroll/navigation
    e.preventDefault();
    e.stopPropagation();
    
    // If drag has started, update position
    // Note: Piece overlay feature is disabled on mobile due to touch handling issues
    if (hasDragStartedRef.current) {
      touchDrag.updateDrag(touch.clientX, touch.clientY);
    }
  }, [piece, isInTray, sourcePosition, touchDrag]);
  
  const handleTouchEnd = useCallback((e) => {
    if (!touchDrag) return;
    
    const touch = e.changedTouches[0];
    
    // Only process if drag actually started
    if (hasDragStartedRef.current) {
      touchDrag.endDrag(touch.clientX, touch.clientY);
    }
    
    // Always prevent default and stop propagation when touch ends on a piece
    e.preventDefault();
    e.stopPropagation();
    
    // Reset state
    setIsTouchDragging(false);
    setIsTouchActive(false);
    touchStartRef.current = null;
    hasDragStartedRef.current = false;
  }, [touchDrag]);
  
  // Handle touch cancel (e.g., when a system gesture interrupts)
  const handleTouchCancel = useCallback((e) => {
    // Reset state without triggering any drop
    setIsTouchDragging(false);
    setIsTouchActive(false);
    touchStartRef.current = null;
    hasDragStartedRef.current = false;
    
    if (touchDrag && touchDrag.cancelDrag) {
      touchDrag.cancelDrag();
    }
  }, [touchDrag]);
  
  // Handle click/tap in tap mode
  const handleClick = useCallback((e) => {
    if (!useTapMode) return;
    
    // Prevent click if we're dragging
    if (isDragging || isTouchDragging) return;
    
    // Don't select if piece is correctly placed
    if (!isInTray && piece.isCorrect) return;
    
    e.stopPropagation();
    onTap?.();
  }, [useTapMode, isDragging, isTouchDragging, isInTray, piece.isCorrect, onTap]);
  
  return (
    <div
      ref={ref}
      onClick={handleClick}
      className={cn(
        "puzzle-piece relative overflow-hidden rounded-sm select-none",
        (isDragging || isTouchDragging) && "opacity-30",
        piece.isCorrect && "correct",
        !piece.isCorrect && piece.isPlaced && !useTapMode && "cursor-grab",
        isInTray && !useTapMode && "cursor-grab transition-all duration-200 ease-out",
        useTapMode && (isInTray || (!piece.isCorrect && piece.isPlaced)) && "cursor-pointer",
        isSelected && "ring-2 ring-primary ring-offset-1 ring-offset-background z-20"
      )}
      style={{
        width: pieceSize,
        height: pieceSize,
        transform: isInTray && isHovered && !isDragging && !isTouchDragging && !useTapMode
          ? `scale(${scale})` 
          : `rotate(${piece.rotation}deg)`,
        transformOrigin: 'center center',
        zIndex: isSelected ? 50 : isHovered ? 50 : (isDragging || isTouchDragging) ? 100 : 1,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        // Disable touch manipulation gestures on puzzle pieces to prevent
        // browser swipe-back navigation when touching pieces
        touchAction: useTapMode ? 'auto' : 'none',
        WebkitTouchCallout: 'none',
      }}
      onMouseEnter={(e) => isInTray && !useTapMode && setIsHovered(isInCenterHotZone(e))}
      onMouseMove={useTapMode ? undefined : handleMouseMove}
      onMouseLeave={() => isInTray && setIsHovered(false)}
      onTouchStart={useTapMode ? undefined : handleTouchStart}
      onTouchMove={useTapMode ? undefined : handleTouchMove}
      onTouchEnd={useTapMode ? undefined : handleTouchEnd}
      onTouchCancel={useTapMode ? undefined : handleTouchCancel}
      draggable={!useTapMode}
    >
      <div
        className="w-full h-full bg-no-repeat pointer-events-none"
        style={{
          backgroundImage: `url(${piece.imageUrl})`,
          backgroundPosition,
          backgroundSize: `${totalSize}px ${totalSize}px`,
          userSelect: 'none',
          // Lighten dark areas to show more detail
          filter: 'brightness(1.1) contrast(1.05)',
        }}
      />
      {/* Piece overlay for better visibility */}
      <div className="absolute inset-0 border border-foreground/10 rounded-sm pointer-events-none" />
      {/* Selected indicator pulse */}
      {isSelected && (
        <div className="absolute inset-0 bg-primary/20 animate-pulse pointer-events-none rounded-sm" />
      )}
    </div>
  );
}

// Custom drag layer that shows the piece while dragging (for desktop)
export function CustomDragLayer() {
  const pieceOverlay = usePieceOverlay();
  const lastCheckTimeRef = useRef(0);
  const hasActivatedOverlayRef = useRef(false);
  
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    currentOffset: monitor.getClientOffset(),
    isDragging: monitor.isDragging(),
  }));
  
  // Reset the activation flag when drag ends
  useEffect(() => {
    if (!isDragging) {
      hasActivatedOverlayRef.current = false;
    }
  }, [isDragging]);
  
  // Check if dragging over reference image and activate overlay
  useEffect(() => {
    if (!isDragging || !item?.piece || !currentOffset || !pieceOverlay) return;
    
    // If overlay is already active, update position (but don't trigger setState cascade)
    if (pieceOverlay.isOverlayActive) {
      // Use requestAnimationFrame to batch updates and prevent infinite loop
      requestAnimationFrame(() => {
        pieceOverlay.updatePiecePosition(currentOffset.x, currentOffset.y);
      });
      return;
    }
    
    // Don't re-activate if we've already activated this drag session
    if (hasActivatedOverlayRef.current) return;
    
    // Throttle checks to avoid performance issues
    const now = Date.now();
    if (now - lastCheckTimeRef.current < 100) return;
    lastCheckTimeRef.current = now;
    
    // Check if current position is over the reference image
    const refImage = document.querySelector('[data-testid="reference-image-thumbnail"]');
    if (!refImage) return;
    
    const rect = refImage.getBoundingClientRect();
    const isOverRef = (
      currentOffset.x >= rect.left &&
      currentOffset.x <= rect.right &&
      currentOffset.y >= rect.top &&
      currentOffset.y <= rect.bottom
    );
    
    if (isOverRef) {
      hasActivatedOverlayRef.current = true;
      pieceOverlay.activateOverlay(item.piece, currentOffset.x, currentOffset.y);
    }
  }, [isDragging, item, currentOffset, pieceOverlay]);
  
  // When drag ends and overlay is active, deactivate it
  useEffect(() => {
    if (!isDragging && pieceOverlay?.isOverlayActive) {
      pieceOverlay.deactivateOverlay();
    }
  }, [isDragging, pieceOverlay]);

  if (!isDragging || !item || !currentOffset) {
    return null;
  }
  
  // Don't render the custom drag layer when piece overlay is active
  if (pieceOverlay?.isOverlayActive) {
    return null;
  }

  const { piece, pieceSize, gridSize = GRID_SIZE } = item;
  const totalSize = pieceSize * gridSize;
  const backgroundPosition = getPieceBackgroundPosition(piece, pieceSize, gridSize);

  return (
    <div
      className="fixed pointer-events-none z-[10000]"
      style={{
        left: 0,
        top: 0,
        transform: `translate(${currentOffset.x - pieceSize / 2}px, ${currentOffset.y - pieceSize / 2}px)`,
      }}
    >
      <div
        className="rounded-sm shadow-xl bg-no-repeat"
        style={{
          width: pieceSize,
          height: pieceSize,
          backgroundImage: `url(${piece.imageUrl})`,
          backgroundPosition,
          backgroundSize: `${totalSize}px ${totalSize}px`,
          transform: `rotate(${piece.rotation}deg) scale(1.05)`,
          border: '2px solid hsl(var(--primary))',
          // Lighten dark areas to show more detail (match PuzzlePiece)
          filter: 'brightness(1.1) contrast(1.05)',
        }}
      />
    </div>
  );
}
